import type { Options } from "@popperjs/core";
import type {
  ComponentProps,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
} from "react";
import {
  cloneElement,
  createContext,
  isValidElement,
  use,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePopper } from "react-popper";

export const calculateHorizontalItemBreakpoint = (
  highestBreakpoint: number,
  size: number,
) => {
  return highestBreakpoint + size;
};

export const calculateHorizontalVisibilityBreakpoint = ({
  itemBreakpoint,
  highestBreakpoint,
  triggerSize,
}: {
  itemBreakpoint: number;
  highestBreakpoint: number;
  triggerSize: number;
}) => {
  return itemBreakpoint === highestBreakpoint
    ? itemBreakpoint
    : itemBreakpoint + triggerSize;
};

type HorizontalItemsContextType = [
  {
    open: boolean;
    triggerSize: number;
    defaultSize: number;
  },
  Dispatch<SetStateAction<{ open: boolean }>>,
];

const HorizontalItemsContext = createContext<HorizontalItemsContextType>([
  {
    open: false,
    triggerSize: 0,
    defaultSize: 0,
  },
  () => {},
]);

// Add this new context for item registration
type HorizontalItemsRegistryContextType = {
  registerItem: (id: string, size?: number) => number;
  unregisterItem: (id: string) => void;
  getHighestBreakpoint: () => number;
  itemRegistry: Map<string, number>;
  registryVersion: number;
};

const HorizontalItemsRegistryContext =
  createContext<HorizontalItemsRegistryContextType>({
    registerItem: () => 0,
    unregisterItem: () => {},
    getHighestBreakpoint: () => 0,
    itemRegistry: new Map<string, number>(),
    registryVersion: 0,
  });

const HorizontalSubItemContext = createContext(false);

// Registry Provider component to handle item registration
export const HorizontalItemsRegistryProvider = ({
  children,
  defaultSize = 0,
}: {
  children: ReactNode;
  defaultSize?: number;
}) => {
  const itemRegistryRef = useRef(new Map<string, number>());
  const [registryVersion, setRegistryVersion] = useState(0);

  const getHighestBreakpoint = useCallback(() => {
    const itemRegistry = itemRegistryRef.current;
    return itemRegistry.size ? Math.max(...itemRegistry.values()) : 0;
  }, []);

  const registerItem = useCallback(
    (id: string, size: number = defaultSize) => {
      const itemRegistry = itemRegistryRef.current;

      if (itemRegistry.has(id)) {
        return itemRegistry.get(id)!;
      }

      const itemBreakpoint = calculateHorizontalItemBreakpoint(
        getHighestBreakpoint(),
        size,
      );
      itemRegistry.set(id, itemBreakpoint);
      setRegistryVersion((version) => version + 1);

      return itemBreakpoint;
    },
    [defaultSize, getHighestBreakpoint],
  );

  const unregisterItem = useCallback((id: string) => {
    if (!itemRegistryRef.current.delete(id)) {
      return;
    }

    setRegistryVersion((version) => version + 1);
  }, []);

  const registryValue = useMemo(
    () => ({
      registerItem,
      unregisterItem,
      getHighestBreakpoint,
      itemRegistry: itemRegistryRef.current,
      registryVersion,
    }),
    [getHighestBreakpoint, registerItem, registryVersion, unregisterItem],
  );

  return (
    <HorizontalItemsRegistryContext value={registryValue}>
      {children}
    </HorizontalItemsRegistryContext>
  );
};

// Main component that uses the registry
export const HorizontalItems = ({
  children,
  containerProps,
  triggerSize = 0,
  defaultSize = 0,
}: {
  children: ReactNode;
  containerProps?: ComponentProps<"div">;
  popoverProps?: ComponentProps<"div">;
  triggerSize?: number;
  defaultSize?: number;
}) => {
  const [state, setState] = useState({
    open: false,
  });

  const finalState = {
    ...state,
    triggerSize,
    defaultSize,
  };

  return (
    <HorizontalItemsRegistryProvider defaultSize={defaultSize}>
      <HorizontalItemsContext value={[finalState, setState]}>
        <div
          style={{
            containerType: "inline-size",
            flexGrow: 1,
            minWidth: "fit-content",
          }}
        >
          <div
            {...containerProps}
            style={{
              position: "relative",
              width: "fit-content",
              ...containerProps?.style,
            }}
          >
            {children}
          </div>
        </div>
      </HorizontalItemsContext>
    </HorizontalItemsRegistryProvider>
  );
};

export const HorizontalItemsPopover = ({
  defaultSize,
  className,
  style,
  popperOptions,
  ...props
}: {
  defaultSize?: number;
  popperOptions?: Partial<Options>;
} & ComponentProps<"div">) => {
  const { getHighestBreakpoint } = use(HorizontalItemsRegistryContext);
  const highestBreakpoint = getHighestBreakpoint();
  const [state] = use(HorizontalItemsContext);

  const id = useId().replace(/:/g, "");
  const popoverClass = `horizontal-items-popover-${id}`;

  const [referenceElement, setReferenceElement] = useState<HTMLDivElement>(
    null!,
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement>(null!);
  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      strategy: "fixed",
      placement: "bottom",
      modifiers: [
        {
          name: "flip",
          options: {
            fallbackPlacements: ["top"],
          },
        },
      ],
      ...popperOptions,
    },
  );

  useEffect(() => {
    if (state.open) {
      update?.();
    }
  }, [state.open, update]);

  return (
    <HorizontalItemsRegistryProvider defaultSize={defaultSize}>
      <HorizontalSubItemContext value={true}>
        <style>
          {`@container (min-width: ${highestBreakpoint}px) { .${popoverClass} { display: none; } }`}
        </style>
        <div
          data-horizontal-items-popover
          className={`
            ${popoverClass}
            ${className}
          `}
          ref={(el) => setPopperElement(el!)}
          {...props}
          style={{
            visibility: state.open ? "visible" : "hidden",
            pointerEvents: state.open ? "auto" : "none",
            ...style,
            ...styles.popper,
            zIndex: 1,
          }}
          {...attributes.popper}
        />
        <div
          ref={(el) => setReferenceElement(el!)}
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0,
          }}
        />
      </HorizontalSubItemContext>
    </HorizontalItemsRegistryProvider>
  );
};

export const HorizontalItem = ({
  children,
  size,
}: {
  children: ReactElement;
  size?: number;
}) => {
  const isSubItem = use(HorizontalSubItemContext);
  const { getHighestBreakpoint, registerItem, unregisterItem } = use(
    HorizontalItemsRegistryContext,
  );
  const [{ triggerSize }] = use(HorizontalItemsContext);
  const id = useId().replace(/:/g, "");
  const [breakpoint, setBreakpoint] = useState(0);

  useLayoutEffect(() => {
    setBreakpoint(registerItem(id, size));

    return () => {
      unregisterItem(id);
    };
  }, [id, registerItem, size, unregisterItem]);

  const highestBreakpoint = getHighestBreakpoint();
  const visibilityBreakpoint = calculateHorizontalVisibilityBreakpoint({
    itemBreakpoint: breakpoint,
    highestBreakpoint,
    triggerSize,
  });

  const selectorClassName =
    "horizontal-item-" + id + "-" + (isSubItem ? "min" : "max");
  const rule = isSubItem
    ? `@container (width > ${visibilityBreakpoint}px)`
    : `@container (width < ${visibilityBreakpoint}px)`;

  return (
    <>
      <style>{`${rule} { .${selectorClassName} { display: none; } }`}</style>
      <div className={selectorClassName}>{children}</div>
    </>
  );
};

export const HorizontalItemsTrigger = ({
  children,
}: {
  children: ReactElement;
}) => {
  const [{ open }, setState] = use(HorizontalItemsContext);
  const isSubItem = use(HorizontalSubItemContext);
  const { getHighestBreakpoint } = use(HorizontalItemsRegistryContext);
  const highestBreakpoint = getHighestBreakpoint();

  const id = useId().replace(/:/g, "");
  const ch = children as ReactElement<ComponentProps<"button">>;

  useEffect(() => {
    const listener = (ev: MouseEvent) => {
      if ((ev.target as HTMLElement).closest("[data-horizontal-items-trigger]"))
        return;

      setTimeout(() => {
        setState((prev) => ({ ...prev, open: false }));
      }, 0);
    };
    if (open) {
      document.addEventListener("click", listener, { capture: true });
    }
    return () => {
      document.removeEventListener("click", listener);
    };
  }, [open]);

  const selectorClassName = "horizontal-item-trigger-" + id;

  // Validate that children is a single element
  if (!isValidElement(children)) {
    throw new Error(
      "HorizontalItemsTrigger requires a single valid React element as children",
    );
  }

  if (isSubItem) {
    return null;
  }

  // Clone the child element and add our props
  const childWithProps = cloneElement(children, {
    onClick: (e) => {
      // Call the original onClick if it exists
      ch.props.onClick?.(e);

      if (!e?.defaultPrevented) {
        setState((prev) => ({ ...prev, open: !prev.open }));
      }
    },
    className: ch.props.className
      ? `${ch.props.className} ${selectorClassName}`
      : selectorClassName,
    "data-open": open,
    "data-closed": !open,
    "data-horizontal-items-trigger": true,
  } as ComponentProps<"button">);

  return (
    <>
      <style>
        {`@container (min-width: ${highestBreakpoint}px) { .${selectorClassName} { display: none; } }`}
      </style>
      {childWithProps}
    </>
  );
};
